import type { AxiosResponse } from "axios";
import { KeycloakInstance } from "keycloak-js";
import { APIErrorKey, HTTPErrorKey } from "translations/types";
import { NestedPartial } from "utils/types";

export interface ClientConfig {
  keycloak: KeycloakInstance;
}

export enum HTTPResponse {
  Continue = 100,
  SwitchingProtocols = 101,
  Processing = 102,
  EarlyHints = 103,
  Ok = 200,
  Created = 201,
  Accepted = 202,
  NonAuthoritativeInformation = 203,
  NoContent = 204,
  ResetContent = 205,
  PartialContent = 206,
  MultiStatus = 207,
  AlreadyReported = 208,
  ImUsed = 226,
  MultipleChoice = 300,
  MovedPermanently = 301,
  Found = 302,
  SeeOther = 303,
  NotModified = 304,
  UseProxy = 305,
  BadRequest = 400,
  Unauthorized = 401,
  PaymentRequired = 402,
  Forbidden = 403,
  NotFound = 404,
  MethodNotAllowed = 405,
  NotAcceptable = 406,
  ProxyAuthenticationRequired = 407,
  RequestTimeout = 408,
  Conflict = 409,
  Gone = 410,
  LengthRequired = 411,
  PreconditionFailed = 412,
  PayloadTooLarge = 413,
  URITooLong = 414,
  UnsupportedMediaType = 415,
  RangeNotSatisfiable = 416,
  ExpectationFailed = 417,
  ImATeapot = 418,
  MisdirectedRequest = 421,
  UnprocessableEntity = 422,
  Locked = 423,
  FailedDependency = 424,
  TooEarly = 425,
  UpgradeRequired = 426,
  PreconditionRequired = 428,
  TooManyRequests = 429,
  RequestHeaderFieldTooLarge = 431,
  UnavailableForLegalReasons = 451,
  InternalServerError = 500,
  NotImplemented = 501,
  BadGateway = 502,
  ServiceUnavailable = 503,
  GatewayTimeout = 504,
  HTTPVersionNotSupported = 505,
  VariantAlsoNegotiates = 506,
  InsufficientStorage = 507,
  LoopDetected = 508,
  NotExtended = 510,
  NetworkAuthorizationRequired = 511,
}

export interface ResponseError {
  key: APIErrorKey | HTTPErrorKey;
  type: "api" | "http";
  message?: string;
  statusCode?: number;
  baseResponse?: AxiosResponse;
}

export type ActionName =
  | "GET_PROFILE"
  | "GET_PAYMENT_HISTORY"
  | "GET_SIGN_IN_METHODS"
  | "REMOVE_SIGN_IN_METHOD"
  | "UPDATE_PROFILE"
  | "CREATE_USER"
  | "RESET_PASSWORD"
  | "CHECK_HEALTH";

export type Resource =
  | "users/me"
  | "users/me/payment-history"
  | "users/me/sign-in-methods"
  | `users/me/sign-in-methods/${string}`
  | "users"
  | "users/me/reset-password"
  | "health";

export interface Action {
  name: ActionName;
  resource: Resource;
  createPayload(payload?: Payload): SerializedPayload;
  method?: AllowedHTTPMethod;
}

// TODO: the linter shouldn't be complaining about key
export type Actions = {
  [_key in ActionName]: Action;
};

export type Response<T = any> = [ResponseError, null] | [null, T];

export type AllowedHTTPMethod = "GET" | "PATCH" | "POST" | "DELETE";

export type SerializedPayload = UserProfilePayload | UserSignupPayload | null;

export type Payload = NestedPartial<UserProfile> | UserSignup | null;

export enum MailingPreference {
  MAIL = "0", // Yes
  ELECTRONIC = "1", // No
  MEMBERSHIP_CARD_ONLY = "2", // Membership card only
}

export enum GenericBooleanOption {
  NO = "0",
  YES = "1",
}

export enum MagazinePreference {
  MAIL = "0",
  ELECTRONIC = "1",
}

export enum UnionMemberStatus {
  NO = "0",
  YES = "1",
  RETIRED = "2",
  FORMER = "3",
}

export enum Gender {
  MAN = "0", // M
  WOMAN = "1", // W
  NON_BINARY = "2", // NB
  GENDER_NON_CONFORMING = "3", // GNC
  OTHER_UNSPECIFIED = "4", // O
}

export interface Phone {
  id: string;
  type: string;
  number: string;
}

export interface Address {
  line1: string;
  line2: string;
  city: string;
  state: string;
  country: string;
  zip: string;
}

export interface SerializedAddress {
  line_1: string;
  line_2: string;
  city: string;
  state: string;
  country: string;
  zip: string;
}

export interface DemographicInfo {
  gender: Gender;
  lgbtq: GenericBooleanOption;
  unionMember: UnionMemberStatus;
  unionLeadership: GenericBooleanOption;
  unionLocal: string;
  unionName: string;
}
export interface SerializedDemographicInfo {
  gender: Gender;
  lgbtq: GenericBooleanOption;
  union_member: UnionMemberStatus;
  union_leadership: GenericBooleanOption;
  union_local: string;
  union_name: string;
}

// This should be kept in sync with the backend version at
// app.lib.local_types.DuesCadence
export type DuesCadence = "monthly" | "annually" | "one_time" | "waived";

// This should be kept in sync with the backend version at
// app.lib.local_types.Dues. Its keys are single words, so the serialized and
// deserialized shapes are identical (no snake/camel divergence).
export interface Dues {
  amount: string;
  cadence: DuesCadence;
  display: string;
}

export type PaymentType = "dues" | "donation";

// A single completed payment, shaped for the membership page. Mirrors the
// backend's app.lib.local_types.PaymentHistoryItem; the client deep-camelCases
// `payment_method` into `paymentMethod`.
export interface PaymentHistoryItem {
  amount: string;
  date: string;
  key: string;
  paymentMethod: string;
  paymentType: PaymentType;
}

// Shape returned by GET users/me/payment-history (unwrapped + camelCased).
export interface PaymentHistory {
  payments: PaymentHistoryItem[];
}

export interface UserProfile {
  uid: string;
  memberStatus: SerializedUserProfile["member_status"];
  // Mirrors app.lib.local_types.UserProfile["in_good_standing"].
  // True only for ActionKit status M; lapsed members have no grace period.
  inGoodStanding: boolean;
  joinDate: string;
  expDate: string;
  email: string;
  firstName: string;
  middleName: string;
  lastName: string;
  billingFirstName: string;
  billingLastName: string;
  chosenFirstName: string;
  chosenLastName: string;
  phones: Phone[];
  dlMailingPreference: MagazinePreference;
  mailingPreference: MailingPreference;
  mailingAddress: Address;
  primaryAddress: Address;
  billingAddress: Address;
  demographicInfo: DemographicInfo;
  dues: Dues | null;
  nextPaymentDate: string | null;
  paymentMethod: string | null;
  // The recurring commitment's credit card expiration date, formatted "MM/YY"
  // (e.g. "04/30") by the backend, or null when there's no active recurring
  // commitment or no card on file (PayPal / one-time payers). Mirrors the
  // backend's app.lib.local_types.UserProfile["card_exp_date"].
  cardExpDate: string | null;
  chapter: string;
  mergedPrimaryEmail?: string | null;
}

// This should be kept in sync with the backend version at
// app.lib.actionkit.types.AKMemberStatus
export enum MemberStatus {
  FORMER_MEMBER = "F",
  LAPSED_MEMBER = "L",
  MEMBER = "M",

  // non-members
  CONTRIBUTOR = "C",
  DECEASED = "D",
  MERGED = "G",
  RES_33_EXPELLED = "E",
  RES_33_RESIGNED = "R",
  SUBSCRIBER = "S",
}

export interface SerializedUserProfile {
  uid: string;
  member_status: MemberStatus;
  in_good_standing: boolean;
  exp_date: string;
  join_date: string;
  email: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  billing_first_name: string;
  billing_last_name: string;
  chosen_first_name: string;
  chosen_last_name: string;
  phones: Phone[];
  dl_mailing_preference: MagazinePreference;
  mailing_preference: MailingPreference;
  mailing_address: Partial<SerializedAddress>;
  primary_address: Partial<SerializedAddress>;
  billing_address: Partial<SerializedAddress>;
  demographic_info: Partial<SerializedDemographicInfo>;
  dues: Dues | null;
  next_payment_date: string | null;
  payment_method: string | null;
  card_exp_date: string | null;
  chapter: string;
  merged_primary_email?: string | null;
}

export interface UserSignup {
  email: string;
}

export interface SignInMethods {
  password: boolean;
  totp: boolean;
  passkey: boolean;
  credentials?: SignInCredentialSummary[];
}

export interface SignInCredentialSummary {
  type: "password" | "totp" | "passkey";
  label?: string | null;
  createdAt?: number | null;
  removalHandle?: string | null;
}

export interface SerializedUserSignup {
  email: string;
  password: string;
  password_confirmation: string;
}

export type ServiceStatus = "OK" | "DOWN";

export interface HealthResponse {
  mode: string;
  status: ServiceStatus;
  version: string;
  workers: string;
  release: {
    branch: string;
    commit: string;
  };
  services: {
    auth: ServiceStatus;
    cache: ServiceStatus;
    database: ServiceStatus;
    membership: ServiceStatus;
  };
}

export type UserProfilePayload = NestedPartial<
  Omit<
    UserProfile,
    | "uid"
    | "memberStatus"
    | "joinDate"
    | "expDate"
    | "billingFirstName"
    | "billingLastName"
    | "billingAddress"
  >
>;

export type UserSignupPayload = UserSignup;
